/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { MessageCircle } from 'lucide-react';

interface Persona {
  id: string;
  name: string;
  title: string;
  description: string;
  [key: string]: any;
}

interface PersonaSelectorProps {
  personas: Persona[];
  selectedPersona: Persona | null;
  onSelectPersona: (persona: Persona) => void;
}

/**
 * PersonaSelector Component
 * Displays a grid of selectable personas with clean, minimal cards using initials
 */
const PersonaSelector: React.FC<PersonaSelectorProps> = ({
  personas,
  selectedPersona,
  onSelectPersona,
}) => {
  // Color mapping based on persona index
  const getColorStyles = (index: number) => {
    const colors = [
      { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500/20' },
      { bg: 'bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400', border: 'border-violet-500/20' },
      { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-500/20' },
      { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/20' },
      { bg: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-500/20' },
      { bg: 'bg-cyan-500/10', text: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-500/20' },
      { bg: 'bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-500/20' },
      { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-500/20' },
    ];
    return colors[index % colors.length];
  };

  // Get initials from name
  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
      {personas.map((persona, index) => {
        const isSelected = selectedPersona?.id === persona.id;
        const colorStyles = getColorStyles(index);

        return (
          <div
            key={persona.id}
            onClick={() => onSelectPersona(persona)}
            className={`bg-card rounded-xl flex flex-col overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group border ${
              isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-text-muted/30'
            }`}
          >
            <div className="p-5 flex-grow flex-1">
              {/* Avatar with initial and name */}
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl ${colorStyles.bg} ${colorStyles.border} border flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-105`}>
                  <span className={`text-lg font-semibold ${colorStyles.text}`}>
                    {getInitials(persona.name)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-foreground mb-1 truncate">{persona.name}</h2>
                  <span className="inline-flex items-center text-xs text-text-muted">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
                    {persona.title}
                  </span>
                </div>
              </div>

              {/* Description */}
              <p className="text-text-secondary text-sm leading-relaxed line-clamp-3 mb-4">
                {persona.description}
              </p>

              {/* Chat button */}
              <div className="mt-auto pt-3 border-t border-border">
                <button className="w-full flex items-center justify-center gap-2 bg-background-secondary hover:bg-background-tertiary text-foreground py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                  <MessageCircle className="w-4 h-4" />
                  Start Chat
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PersonaSelector;
